import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  setOrganizationAlertStatus,
  setOrganizationAlertComment,
} from '@kirin/api-client/api/alerts';
import type {
  AlertFeedbackState,
  AlertFeedbackStatus,
} from '@kirin/api-client/contracts/alerts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from './queryKeys';

/** Variables for {@link useSetAlertStatus}. */
export interface SetAlertStatusVariables {
  alertKey: string;
  status: AlertFeedbackStatus;
}

/** Variables for {@link useSetAlertComment}. */
export interface SetAlertCommentVariables {
  alertKey: string;
  comment: string;
}

/**
 * KIR-4440. Invalidate every cached page of the org grouped-alerts list so the
 * updated feedback fragment is refetched. We invalidate on the
 * `[...organizations.all, 'groupedAlerts']` prefix (params-agnostic) rather than
 * patching the cache optimistically — the server owns the setter/timestamp
 * truth, and a single refetch is simpler than mirroring that here. The alert
 * occurrences read and the notification landing's ref read carry the same
 * fragment (the occurrences modal, the alert window and the landing render
 * Status and Comment from them), so they are refetched too.
 */
async function invalidateGroupedAlerts(
  queryClient: ReturnType<typeof useQueryClient>
): Promise<void> {
  await Promise.all(
    (['groupedAlerts', 'alertOccurrences', 'alertRef'] as const).map(kind =>
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.organizations.all, kind],
      })
    )
  );
}

/**
 * React Query mutation to set the feedback status verdict on an org alert.
 * Manager/owner only — gating belongs at the call site; the route 404s other
 * roles and when the `alert_feedback` flag is off, so misuse fails closed.
 */
export function useSetAlertStatus(
  organizationId: string
): UseMutationResult<AlertFeedbackState, Error, SetAlertStatusVariables> {
  const queryClient = useQueryClient();

  return useMutation<AlertFeedbackState, Error, SetAlertStatusVariables>({
    mutationFn: ({ alertKey, status }) =>
      setOrganizationAlertStatus(apiClient, organizationId, alertKey, {
        status,
      }),
    onSettled: () => invalidateGroupedAlerts(queryClient),
  });
}

/**
 * React Query mutation to set the free-text comment on an org alert. Any org
 * member (excluding support) may edit; an empty string clears the comment.
 */
export function useSetAlertComment(
  organizationId: string
): UseMutationResult<AlertFeedbackState, Error, SetAlertCommentVariables> {
  const queryClient = useQueryClient();

  return useMutation<AlertFeedbackState, Error, SetAlertCommentVariables>({
    mutationFn: ({ alertKey, comment }) =>
      setOrganizationAlertComment(apiClient, organizationId, alertKey, {
        comment,
      }),
    onSettled: () => invalidateGroupedAlerts(queryClient),
  });
}
